import {OidcMetadata, User, UserManager, UserManagerSettings} from 'oidc-client';
import {useSystemStore} from "@/stores/system/SystemStore";
import {$t} from "@/utilities/system/i18n/config";
import {notify} from "@kyvg/vue3-notification";


// helper class to not have multiple userManagerInstances across multiple OAuthServiceInstances
// pseudo singleton to avoid event-calling overflow
// which helps with token management
// TODO needs to be reworked when SSO is implemented, since we have multiple identity-providers in that case
class StaticUserManager {
    private static globalInstance: UserManager|null = null;
    //TODO hard reset here?

    public static getUserManager(settings: UserManagerSettings):UserManager {
        if(this.globalInstance) return this.globalInstance;

        this.globalInstance = new UserManager(settings);
        this.globalInstance.events.addAccessTokenExpiring(this.tokenExpiringEvent)
        this.globalInstance.events.addAccessTokenExpired(this.tokenExpiredEvent)

        return this.globalInstance;
    }

    // renew tokens when they are close to expiring (set by Setting 'accessTokenExpiringNotificationTime')
    private static async tokenExpiringEvent(){
        const instance = OAuthService.getDefaultInstance()
        try {
            await instance.userManager.signinSilent() // refreshes tokens
        }
        catch (error:any) {
            if(error.message === "invalid_grant"){
                console.log("token refresh failed", error)
            }
        }
    }

    // called when tokens are invalid due to expiration
    private static async tokenExpiredEvent(){
        notify({
            title: $t('general.token.expired.title'),
            text: $t('general.token.expired.text'),
            duration: -1,
            type: "error"
        });
    }
}

export default class OAuthService {
    public userManager: UserManager;

    public static getDefaultInstance():OAuthService {
        const systemStore = useSystemStore();

        const clientId = systemStore.settings.startupConfig?.clientId as string;
        const scopes = systemStore.settings.startupConfig?.clientScopes as string;
        const callbackUrls = systemStore.getCallbackUrls();
        const ssoCallback = callbackUrls.normal;
        const ssoSilentCallback = callbackUrls.silent;

        return new OAuthService(systemStore.getAuthorityUrl(), clientId, scopes, ssoCallback, ssoSilentCallback);
    }

    constructor(authority:string, clientId:string, scopes:string, callback:string, silentCallback:string, metadata:OidcMetadata|undefined = undefined) {
        const settings: UserManagerSettings = {
            authority: authority,
            client_id: clientId,
            redirect_uri: window.location.origin + callback,
            silent_redirect_uri: window.location.origin + silentCallback,
            accessTokenExpiringNotificationTime: 10, //seconds
            response_type: 'code',
            scope: scopes,
            post_logout_redirect_uri: window.location.origin + '/logout',
            filterProtocolClaims: false,
            revokeAccessTokenOnSignout: true,
            metadata: metadata,
        };

        this.userManager = StaticUserManager.getUserManager(settings);
    }

    public async login():Promise<void> {
        return await this.userManager.signinRedirect();
    }

    public async logout():Promise<void> {
        return await this.userManager.signoutRedirect();
    }

    public async loginSilent():Promise<User | undefined> {
        return await this.userManager.signinSilentCallback();
    }

    public async refreshTokens():Promise<User|null>{
        return await this.userManager.getUser()
    }
}

